import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionCard from '@/components/feature/SectionCard';
import MatchRow from '@/components/feature/MatchRow';
import TeamBadge from '@/components/base/TeamBadge';
import { useSEO } from '@/hooks/useSEO';
import { getMatchesByTeam, getPlayersByTeam, getTeam } from '@/utils/data';

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const team = getTeam(teamId);

  useSEO({
    title: team ? `${team.name} 队资料 - 篮球比分` : '球队不存在 - 篮球比分',
    description: team ? `${team.name}球队简介、赛季排名、近期战绩与主要球员名单等资料汇总。` : '未找到对应的球队信息。',
    keywords: team ? `${team.name},球队资料,篮球球队,${team.leagueName}` : '篮球球队',
  });

  if (!team) {
    return (
      <div className="px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-team-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-900">球队不存在</h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-md">未找到该球队的资料，可能已被移除或链接有误。</p>
        <Link to="/teams" className="mt-6 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer whitespace-nowrap">
          浏览全部球队
        </Link>
      </div>
    );
  }

  const roster = getPlayersByTeam(team.id);
  const teamMatches = getMatchesByTeam(team.id);

  const statCards = [
    { label: '场均得分', value: team.avgPoints, icon: 'ri-basketball-line' },
    { label: '场均失分', value: team.avgAllowed, icon: 'ri-shield-line' },
    { label: '防守效率', value: team.defRating, icon: 'ri-bar-chart-2-line' },
    { label: '胜率', value: team.winRate, icon: 'ri-percent-line' },
  ];

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '球队', to: '/teams' },
          { label: team.name },
        ]}
      />

      <section className="mt-4 rounded-lg border border-background-200/80 overflow-hidden">
        <div className="h-20 md:h-24" style={{ backgroundColor: team.color }}></div>
        <div className="px-5 md:px-8 pb-6 -mt-10">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="rounded-full ring-4 ring-background-50">
              <TeamBadge team={team} size="lg" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{team.name}</h1>
              <p className="mt-1 text-sm text-foreground-600">
                {team.leagueName} · {team.city} · 主教练 {team.coach}
              </p>
            </div>
            <Link
              to={`/category/${team.leagueId}`}
              className="h-10 px-4 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap self-start sm:self-auto"
            >
              查看该联赛赛程
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>

          <p className="mt-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed max-w-4xl">{team.intro}</p>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="rounded-md bg-background-100 px-3 py-2">
              <span className="block text-xs text-foreground-500">成立年份</span>
              <span className="font-heading font-bold text-foreground-900">{team.founded} 年</span>
            </div>
            <div className="rounded-md bg-background-100 px-3 py-2">
              <span className="block text-xs text-foreground-500">主场球馆</span>
              <span className="font-medium text-foreground-900">{team.arena}</span>
            </div>
            <div className="rounded-md bg-background-100 px-3 py-2">
              <span className="block text-xs text-foreground-500">本赛季战绩</span>
              <span className="font-heading font-bold text-foreground-900">{team.win} 胜 {team.loss} 负</span>
            </div>
            <div className="rounded-md bg-background-100 px-3 py-2">
              <span className="block text-xs text-foreground-500">联赛排名</span>
              <span className="font-heading font-bold text-primary-600">第 {team.rank} 位</span>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="近期比赛" icon="ri-calendar-2-line" moreTo={`/category/${team.leagueId}`} moreText="全部赛事">
            {teamMatches.length > 0 ? (
              <div>
                {teamMatches.map((m) => (
                  <MatchRow key={m.id} match={m} />
                ))}
              </div>
            ) : (
              <p className="px-4 md:px-5 py-10 text-center text-sm text-foreground-500">暂无比赛记录。</p>
            )}
          </SectionCard>

          <SectionCard title="主要球员" icon="ri-user-star-line">
            {roster.length > 0 ? (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {roster.map((p) => (
                  <Link
                    key={p.id}
                    to={`/player/${p.id}`}
                    className="flex items-center gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                  >
                    <span className="w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-white shrink-0" style={{ backgroundColor: team.color }}>
                      {p.number}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{p.name}</p>
                      <p className="text-xs text-foreground-500">{p.position} · 场均 {p.ppg} 分</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 ml-auto"></i>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="px-4 md:px-5 py-10 text-center text-sm text-foreground-500">暂无球员资料。</p>
            )}
          </SectionCard>
        </div>

        <aside className="space-y-6">
          <SectionCard title="球队数据" icon="ri-line-chart-line">
            <div className="p-4 grid grid-cols-2 gap-3">
              {statCards.map((s) => (
                <div key={s.label} className="rounded-md bg-background-100/70 border border-background-200/60 px-3 py-4">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-2">
                    <i className={s.icon}></i>
                  </span>
                  <span className="block font-heading font-extrabold text-xl text-foreground-950">{s.value}</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">{s.label}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="赛季排名" icon="ri-trophy-line">
            <div className="px-4 py-4">
              <div className="flex items-center justify-between rounded-md bg-accent-50 border border-accent-200 px-4 py-3">
                <span className="text-sm text-foreground-700">{team.leagueName} 积分榜</span>
                <span className="font-heading font-extrabold text-2xl text-accent-700">第 {team.rank} 位</span>
              </div>
              <p className="mt-3 text-xs text-foreground-500 leading-relaxed">
                本赛季截至目前取得 {team.win} 胜 {team.loss} 负，胜率 {team.winRate}，场均净胜分 {(team.avgPoints - team.avgAllowed).toFixed(1)} 分。
              </p>
            </div>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}